// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Platform, Pressable, StyleSheet } from 'react-native';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import { newPhotoId } from '../newPhotoId';
import { pickFromCamera } from '../pickPhotos';
import {
  enqueuePhotoUpload,
  setPhotoUploadAuthToken,
  subscribeToPhotoUploads,
} from '../uploadQueue';

type HolePhotoButtonProps = {
  tourId: string | null | undefined;
  roundId: string | null | undefined;
  holeNumber: number;
  token: string | null | undefined;
};

/**
 * Camera button for the hole screen header — the primary way photos get taken, because players are
 * standing on the hole when something worth photographing happens.
 *
 * Shoot-and-go by design: capture, dismiss, and the upload continues in the background with the
 * round and hole filled in from context. No caption prompt and no progress modal — nobody wants a
 * text field while holding a putter, and captions can be added later from the gallery.
 *
 * All upload state lives in the module-scoped queue, never in this component or its parent: the
 * hole screen unmounts the instant a player advances to the next hole, which is exactly when
 * someone who just took a photo is most likely to move on.
 */
export default function HolePhotoButton({
  tourId,
  roundId,
  holeNumber,
  token,
}: HolePhotoButtonProps) {
  const { t } = useI18n();
  const [capturing, setCapturing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const reportedErrorRef = useRef<string | null>(null);

  // A note rendered inside a header button is clipped to nothing, so problems have to be reported
  // out of band. An Alert is intrusive, but a photo that silently fails to upload is worse: the
  // player walks on believing it was saved. The ref keeps one failure to one alert — the queue holds
  // lastError until the next enqueue, so every later notification would otherwise re-fire it.
  useEffect(
    () =>
      subscribeToPhotoUploads((state) => {
        setPendingCount(state.pending.length);
        if (state.lastError && state.lastError !== reportedErrorRef.current) {
          reportedErrorRef.current = state.lastError;
          Alert.alert(t('tourPhotos.uploadFailedTitle'), t('tourPhotos.uploadFailed'));
        }
        if (!state.lastError) {
          reportedErrorRef.current = null;
        }
      }),
    [t],
  );

  useEffect(() => {
    setPhotoUploadAuthToken(token);
  }, [token]);

  const capture = useCallback(async () => {
    if (!tourId || capturing) {
      return;
    }

    setCapturing(true);
    try {
      const outcome = await pickFromCamera();
      if (outcome.kind === 'permission-denied') {
        Alert.alert(t('tourPhotos.uploadFailedTitle'), t('tourPhotos.cameraDenied'));
        return;
      }
      if (outcome.kind === 'rejected') {
        Alert.alert(
          t('tourPhotos.uploadFailedTitle'),
          outcome.reason === 'too-large' ? t('tourPhotos.tooLarge') : t('tourPhotos.unsupportedType'),
        );
        return;
      }
      if (outcome.kind === 'cancelled') {
        return;
      }

      outcome.photos.forEach((photo) => {
        enqueuePhotoUpload({
          id: newPhotoId(),
          tourId,
          // Tagged from context, which is the whole point of capturing here: no round picker, no
          // hole picker, no post-hoc data entry.
          roundId: roundId ?? null,
          holeNumber,
          caption: null,
          photo,
        });
      });
    } finally {
      setCapturing(false);
    }
  }, [capturing, holeNumber, roundId, t, tourId]);

  // A round outside a tour has nowhere to file a photo.
  if (!tourId) {
    return null;
  }

  // Native only: web has no camera here, and the hole screen is a phone surface in practice.
  if (Platform.OS === 'web') {
    return null;
  }

  return (
    <Pressable
      onPress={() => void capture()}
      disabled={capturing}
      accessibilityRole="button"
      accessibilityLabel={t('tourPhotos.captureAction')}
      testID="hole-photo-capture"
      style={({ pressed }) => [styles.button, pressed && styles.buttonPressed]}
      hitSlop={8}
    >
      {capturing ? (
        <ActivityIndicator size="small" color={appColors.accent.primary} />
      ) : (
        <Text style={styles.icon}>{'▣'}</Text>
      )}
      {/* How many uploads are still in flight, so the player can see the photo is on its way rather
          than having to trust it. Failures come through as an Alert — see the subscription above. */}
      {pendingCount > 0 ? <Text style={styles.badge}>{pendingCount}</Text> : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginRight: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'rgba(161, 212, 147, 0.45)',
    backgroundColor: 'rgba(20, 62, 31, 0.6)',
  },
  buttonPressed: {
    backgroundColor: 'rgba(161, 212, 147, 0.2)',
  },
  icon: {
    color: '#C6E3B8',
    fontSize: 15,
    fontWeight: '700',
  },
  badge: {
    color: '#C6E3B8',
    fontSize: 11,
    fontWeight: '700',
  },
});
